import React from "react";
import { Link } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiPhone, mdiWhatsapp } from "@mdi/js";
import Layout from "../components/Layout";
import Seo, { businessJsonLd } from "../components/Seo";
import SectionHeading from "../components/SectionHeading";
import SavingsCalculator from "../components/SavingsCalculator";
import LocationBand from "../components/LocationBand";
import Faq from "../components/Faq";
import { services } from "../content/services";
import { sortedPosts, formatDate } from "../content/posts";
import { useSite } from "../theme/ThemeProvider";

const HOME_FAQ = [
  {
    q: "Randevu almam gerekiyor mu?",
    a: "Yağ değişimi, filtre ve küçük kontroller için randevusuz gelebilirsiniz. LPG montajı ve tank değişimi için bir gün önceden aramanız yeterli; araç genellikle aynı gün teslim edilir.",
  },
  {
    q: "Hangi LPG markalarını takıyorsunuz?",
    a: "Ağırlıklı olarak Atiker ve Zavoli takıyoruz; ikisinin de yedek parçası atölyede bulunur. Lovato, BRC ve diğer markalarda da montaj yaparız. Marka fark etmeksizin her sistemin bakımı ve tamiri yapılır.",
  },
  {
    q: "Fiyat ne kadar?",
    a: "Araca, seçilen sisteme ve tanka göre değişiyor; o yüzden sitede tek fiyat yazmıyoruz. Marka, model ve yılı yazarsanız aynı gün net fiyat söyleriz.",
  },
  {
    q: "Muayene için sızdırmazlık raporu veriyor musunuz?",
    a: "Evet. Kontrolü yapıp raporu aynı gün hazırlıyoruz. Bizde montaj yapılmamış araçlara da veriyoruz.",
  },
];

const Home = () => {
  const site = useSite();
  const posts = sortedPosts.slice(0, 4);

  return (
    <Layout>
      <Seo
        description="Yalova Altınova Kaytazdere'de LPG montaj, LPG tank değişimi, LPG bakım, sızdırmazlık raporu, motor yağı değişimi ve çeki demiri montajı. 30 yıllık usta, Atiker ve Zavoli sistemler."
        path="/"
        jsonLd={businessJsonLd(site)}
      />

      <section className="intro">
        <div className="container">
          <div>
            <h1>Kaytazdere'de LPG ve araç servisi</h1>
            <p className="intro__text">
              Sıralı sistem LPG montajı, tank değişimi, LPG bakımı ve sızdırmazlık raporu. Yeni yerimizde motor yağı
              değişimi ve çeki demiri montajı da yapıyoruz. Yalova–Kocaeli yolu üzerindeyiz.
            </p>
            <div className="intro__actions">
              <Link to="/iletisim" className="btn btn--primary">
                Fiyat / randevu isteyin
              </Link>
              <a href={`tel:${site.phoneHref}`} className="phone">
                <Icon path={mdiPhone} /> {site.phone}
              </a>
              <a href={`https://wa.me/${site.whatsapp}`} target="_blank" rel="noreferrer" className="phone">
                <Icon path={mdiWhatsapp} /> WhatsApp
              </a>
            </div>
            <div className="intro__facts">
              <span>{site.founder.experienceYears} yıldır bu işteyiz</span>
              <span>Atiker ve Zavoli yetkili montaj</span>
              <span>{site.hours[0].days} {site.hours[0].time}</span>
            </div>
          </div>
          <figure className="intro__photo">
            <img src="/images/services/lpg-montaj.jpg" alt="Motor bölümünde LPG enjektör montajı" />
            <figcaption>Atölyeden: sıralı sistem enjektör bağlantısı.</figcaption>
          </figure>
        </div>
      </section>

      <section className="section section--tight">
        <div className="container">
          <div className="note">
            <p>
              "İstanbul'da başarıyla sürdürdüğümüz 30 yılın ardından, Kaytazdere'deki iş yerimizde çalışma hayatımıza
              devam ediyoruz. Eski müşterilerimize güvenleri için teşekkür ederiz; Yalova ve çevresindeki yeni
              komşularımızı da bekleriz. Yeni yerimiz {formatDate(site.announcement.openingDate)} tarihinde açılıyor."
            </p>
            <footer>
              {site.founder.name}, {site.founder.title}
            </footer>
          </div>
        </div>
      </section>

      <section className="section" id="hizmetler">
        <div className="container">
          <SectionHeading title="Yaptığımız işler" />
          <ul className="service-list">
            {services.map((s) => (
              <li key={s.slug}>
                <Link to={`/hizmetler/${s.slug}`}>
                  <h3>
                    {s.title}
                    {s.isNew && <span className="badge badge--new">yeni</span>}
                  </h3>
                  <p>{s.short}</p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section section--surface" style={{ borderTop: "1px solid var(--color-border)", borderBottom: "1px solid var(--color-border)" }}>
        <div className="container">
          <div className="split">
            <div>
              <SectionHeading title="Nasıl çalışıyoruz" />
              <ul className="plain-list">
                <li>Montajdan önce aracı inceler, hangi sistemin ve tankın uyacağını fiyatıyla birlikte söyleriz.</li>
                <li>Montaj bittikten sonra aracı 22 noktadan kontrol eder, yol testine çıkarız. Sorun görürsek teslim etmeyiz.</li>
                <li>Sızdırmazlık raporunu ve muayene için gereken belgeleri biz hazırlarız.</li>
                <li>İşçiliğe 2 yıl garanti veririz. İlk 1.000 km'den sonra ücretsiz kontrol ve ayar yaparız.</li>
                <li>Yedek parçayı stokta tutarız; filtre ya da regülatör için günlerce beklemezsiniz.</li>
              </ul>
            </div>
            <figure className="split__media">
              <img src="/images/services/lpg-tank.jpg" alt="Bagaja monte edilmiş silindir LPG tankı" loading="lazy" />
              <figcaption>Silindir tank ve multivalf. Bagaj hacmine göre simit tank da takıyoruz.</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <SavingsCalculator />

      <section className="section section--surface" style={{ borderTop: "1px solid var(--color-border)" }}>
        <div className="container">
          <div className="split" style={{ alignItems: "start" }}>
            <div>
              <SectionHeading title="Sık sorulanlar" />
              <Faq items={HOME_FAQ} />
            </div>
            <div>
              <SectionHeading title="Yazılar" text="Araç sahipleri için kısa rehberler." />
              <ul className="post-list">
                {posts.map((p) => (
                  <li key={p.slug}>
                    <Link to={`/blog/${p.slug}`}>
                      <time dateTime={p.date}>{formatDate(p.date)}</time>
                      <h3>{p.title}</h3>
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-2">
                <Link to="/blog">Tüm yazılar →</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <LocationBand />
    </Layout>
  );
};

export default Home;
